<template>
  <v-sheet class="mx-auto" max-width="2000">
    <v-card class="mx-auto" color="primary" variant="plain" elevation="5" title="Overall survival Kaplan-Meier estimates">
      <v-row no-gutters>
        <v-col v-for="(number, index) in survivalNumbers" :key="index">
          <v-sheet class="pa-2 ma-2">
            <div class="number-box">
              <transition name="fade-bounce" mode="out-in">
                <div :key="number">
                  <span class="large-number">{{ number.value }}</span>
                  <span class="percentage-number" v-if="number.percentage">&nbsp;{{ number.percentage }}%</span>
                </div>
              </transition>
              <div class="label large-number-label">{{ number.label }}</div>
            </div>
          </v-sheet>
        </v-col>
        <v-col>
          <v-sheet class="pa-2 ma-2">
            <div class="number-box-left">
              <transition name="fade" mode="out-in">
                <div :key="medianFollowUpTimeData">
                  <v-data-table :headers="medianFollowUpTimeHeaders" :items="medianFollowUpTimeData"
                                :fixed-header="true"
                                height="200px"
                                :items-per-page="0">
                    <template #bottom></template>
                  </v-data-table>
                </div>
              </transition>
            </div>
          </v-sheet>
        </v-col>
        <v-col>
          <v-select class="mt-10 mr-5" v-model="selected_stratify_items" label="Comparison groups"
                    :items="select_stratify_items" :multiple="true">
            <template #item="{ props }">
              <v-list-subheader v-if="props.header">{{ props.header }}</v-list-subheader>
              <v-divider v-else-if="props.divider" class="mt-2" />
              <v-list-item v-else v-bind="props" class="ml-2"></v-list-item>
            </template>
          </v-select>
          <v-checkbox v-model="kaplan_meier_confidence_intervals" label="Show confidence intervals"
                      hide-details></v-checkbox>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col>
          <v-card class="mx-auto" min-height="800" color="primary" variant="plain" elevation="5">
            <GChart type="LineChart" :data="survivalChartData"
                    :options="survivalChartOptions"
                    v-if="survivalChartData.length !== 0"/>
            <v-chip variant="elevated" size="x-large" density="default" v-else>
              No data to display. Please adjust your filters.
            </v-chip>
          </v-card>
        </v-col>
      </v-row>
    </v-card>
  </v-sheet>
</template>

<script>
import {defineComponent} from 'vue';
import {GChart} from 'vue-google-charts'
import axios from "axios";

export default defineComponent({
  name: 'CancerOverallSurvival',
  props: {
    icd: String,
    ageAtDiagnosis: Array,
    diagnosisDate: Array,
    survivalTimeInYears: Array,
    metastasisStatusM: Array,
    metastasisStatusN: Array,
    atcCodes: Array,
    visible: Boolean
  },
  components: {
    GChart
  },
  data: () => ({
    kaplan_meier_confidence_intervals: false,
    select_atc_items: [],
    select_stratify_items: [],
    selected_stratify_items: ['Currently filtered'],
    survivalNumbers: [],
    medianFollowUpTimeHeaders: [
      {
        title: 'Comparison group',
        align: 'start',
        key: 'comparison_group',
      },
      {
        title: 'Median follow-up time (months)',
        align: 'end',
        key: 'median_follow_up_time',
      }
    ],
    medianFollowUpTimeData: [],
    survivalChartData: [],
    survivalChartOptions: {
      title: '',
      backgroundColor: {
        fill: '#364c5b'
      },
      height: 800,
      colors: ['#a6cee3', '#b2df8a', '#fb9a99', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c', '#bdbdc7'],
      titleTextStyle: {
        fontSize: 16,
        color: '#ffffff',
        bold: true
      },
      legend: {
        position: 'bottom',
        alignment: 'end',
        textStyle: {color: '#c0c0c0', fontSize: 16},
        scrollArrows: {
          activeColor: '#f0f0f0',
          inactiveColor: 'c0c0c0'
        },
        pagingTextStyle: {
          color: '#f0f0f0'
        }
      },
      hAxis: {
        title: 'Follow-up time (months)',
        baselineColor: '#f0f0f0',
        titleTextStyle: {
          color: '#f0f0f0',
          italic: false
        },
        textStyle: {
          color: '#ffffff'
        },
        gridlines: {
          count: 0
        },
        minorGridlines: {
          count: 0
        }
      },
      vAxis: {
        title: 'Probability of survival',
        baselineColor: '#f0f0f0',
        titleTextStyle: {
          color: '#f0f0f0',
          italic: false
        },
        textStyle: {
          color: '#ffffff'
        },
        gridlines: {
          color: '#c0c0c0',
          interval: 0.5
        },
        minorGridlines: {
          count: 0
        }
      },
      intervals: { 'style': 'area' },
      tooltip: {
        isHtml: true
      }
    }
  }),
  watch: {
    selected_stratify_items() {
      this.loadDynamicData()
    },
    kaplan_meier_confidence_intervals() {
      this.loadDynamicData()
    }
  },
  methods: {
    updateSelectStratifyItems() {
      let updated_select_stratify_items = [
        {
          title: 'Currently filtered'
        },
        { props: { divider: true } },
        { props: { header: 'Age at diagnosis' } }
      ]
      this.ageAtDiagnosis.forEach(element =>
        updated_select_stratify_items.push({
          title: element
        }))
      updated_select_stratify_items.push({
        props: { divider: true }
      })
      updated_select_stratify_items.push({
        props: { header: 'Year of diagnosis' }
      })
      this.diagnosisDate.forEach(element =>
          updated_select_stratify_items.push({
            title: element
          }))
      if (this.metastasisStatusM.length > 0) {
        updated_select_stratify_items.push({
          props: {divider: true}
        })
        updated_select_stratify_items.push({
          props: {header: 'Metastasis status (M)'}
        })
        this.metastasisStatusM.forEach(element =>
            updated_select_stratify_items.push({
              title: element
            }))
      }
      if (this.metastasisStatusN.length > 0) {
        updated_select_stratify_items.push({
          props: { divider: true }
        })
        updated_select_stratify_items.push({
          props: { header: 'Metastasis status (N)' }
        })
        this.metastasisStatusN.forEach(element =>
            updated_select_stratify_items.push({
              title: element
            }))
      }
      if (this.select_atc_items.length > 0) {
        updated_select_stratify_items.push({
          props: {divider: true}
        })
        updated_select_stratify_items.push({
          props: {header: 'ATC'}
        })
        if (this.atcCodes.length === 0) {
          this.select_atc_items.forEach(element => {
            updated_select_stratify_items.push({
              title: element
            });
          })
        } else {
          this.atcCodes.forEach(element => {
            updated_select_stratify_items.push({
              title: element
            });
          })
        }
      }

      this.select_stratify_items = updated_select_stratify_items
    },
    updateSelectedStratifyItems() {
      this.selected_stratify_items = this.selected_stratify_items.filter(element =>
          element === 'Currently filtered' ||
          this.ageAtDiagnosis.includes(element) || this.diagnosisDate.includes(element) ||
          this.metastasisStatusM.includes(element) || this.metastasisStatusN.includes(element) ||
          (this.select_atc_items.includes(element) && ((this.atcCodes.length === 0) || this.atcCodes.includes(element))))
    },
    async loadSurvivalNumbers() {
      return axios.get('cancer-survival-numbers',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              survival_time_in_years: this.survivalTimeInYears.join(","),
              metastasis_status_m: this.metastasisStatusM.join(","),
              metastasis_status_n: this.metastasisStatusN.join(","),
              atc_codes: this.atcCodes.join(",")
            }
          });
    },
    async loadMedianFollowUpTimeData() {
      return axios.get('cancer-median-follow-up-time',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              survival_time_in_years: this.survivalTimeInYears.join(","),
              metastasis_status_m: this.metastasisStatusM.join(","),
              metastasis_status_n: this.metastasisStatusN.join(","),
              atc_codes: this.atcCodes.join(","),
              stratify_items: this.selected_stratify_items.join(",")
            }
          });
    },
    async loadSurvivalData() {
      return axios.get('cancer-survival',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              survival_time_in_years: this.survivalTimeInYears.join(","),
              metastasis_status_m: this.metastasisStatusM.join(","),
              metastasis_status_n: this.metastasisStatusN.join(","),
              atc_codes: this.atcCodes.join(","),
              stratify_items: this.selected_stratify_items.join(","),
              kaplan_meier_confidence_intervals: this.kaplan_meier_confidence_intervals
            }
          });
    },
    async loadAtcCodes() {
      return axios.get('/cancer-atc-codes',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
    },
    async loadStaticData() {
      return await this.loadAtcCodes().then((response) => {
        this.select_atc_items = response.data
        console.log('Static data loaded: CancerOverallSurvival')
      })
    },
    async loadDynamicData() {
      this.updateSelectStratifyItems()
      await Promise.all([
        this.loadSurvivalNumbers(),
        this.loadMedianFollowUpTimeData(),
        this.loadSurvivalData()
      ]).then(responses => {
        this.survivalNumbers = responses[0].data
        this.medianFollowUpTimeData = responses[1].data
        this.survivalChartData = responses[2].data
        console.log('Dynamic data loaded: CancerOverallSurvival')
      })
    }
  },
  mounted() {
    this.loadStaticData().then(() => {
      return this.loadDynamicData()
    })
  },
  updated() {
    if (this.visible) {
      this.updateSelectedStratifyItems()
    }
  }
});
</script>

<style scoped>
.number-box {
  justify-content: space-around;
  text-align: center;
  padding: 20px;
  margin: 10px;
  flex: 1;
  border-radius: 8px;
  background-color: #364c5b;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1), 0 0 0 2px #c0c0c0; /* Elevated border effect */
}

.number-box-left {
  justify-content: space-around;
  text-align: left;
  padding: 20px;
  margin: 10px;
  flex: 1;
  border-radius: 8px;
  background-color: #364c5b;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1), 0 0 0 2px #c0c0c0; /* Elevated border effect */
}

.large-number {
  font-size: 4em;
  font-weight: bold;
}

.percentage-number {
  font-size: 2em;
  color: #b2df8a;
}

.large-number-label {
  font-size: 1em;
  margin-top: 10px;
}

.fade-bounce-enter-active {
  transition: all 0.5s ease-in;
  animation: bounce-in 0.5s;
}
.fade-bounce-leave-active {
  transition: all 0.5s ease-out;
  animation: bounce-in 0.5s reverse;
}

.fade-bounce-enter-from,
.fade-bounce-leave-to {
  opacity: 0;
}

@keyframes bounce-in {
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.25);
  }
  100% {
    transform: scale(1);
  }
}
</style>
